import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Button, { TYPES } from '../../Button/index'
import IconV2 from '../../Icon.v2/index'
import { selectOnboardingGenericTerms, selectOnboardingTopics } from '../../../services/onboarding/selectors'
import { sendOptimizelyFeatureEvent } from '../../../services/optimizely-feature/actions'
import OptimizelyFeature from '../../OptimizelyFeature/index'
import OnboardingTitleGradient from '../OnboardingTitleGradient'
import OnboardingProgressBar from '../OnboardingProgressBar'

/**
 * Onboarding Topic
 * @param {Object} props - The props
 * @param {Map} props.topic - Current Topic
 * @param {List} props.selectedTopics - List of selected Topics
 * @param {Function} props.onSelect - Function to set selected topic
 * @returns {JSX.Element} - Topic component
 */
const Topic = ({ topic, selectedTopics, onSelect }) => {
  const index = selectedTopics.findIndex((selectedTopic) => selectedTopic.get('tid') === topic.get('tid'))
  const isSelected = index !== -1

  const className = isSelected
    ? 'onboarding-topics__list-item onboarding-topics__list-item--selected'
    : 'onboarding-topics__list-item'

  const title = topic.get('title')
  const iconKey = topic.get('iconKey')

  return (
    <li onClick={onSelect} className={className} key={title}>
      {isSelected ? (
        <span className="onboarding-topics__list-item--number">
          {index + 1}
        </span>
      ) : (
        <span><IconV2 type={iconKey} /></span>
      )}
      {title}
    </li>
  )
}

/**
 * List of Onboarding Topics
 * @param {Object} props - The props
 * @param {List} props.selectedTopics - List of selected Topics
 * @param {Function} props.setTopics - Function to set selected topic
 * @returns {JSX.Element} - Topics List component
 */
const TopicsList = ({ setTopics, selectedTopics }) => {
  const onboardingTopics = useSelector(selectOnboardingTopics)

  if (!onboardingTopics.size) {
    return null
  }

  return (
    <div className="onboarding-topics__container">
      <ul className="onboarding-topics__list">
        {onboardingTopics.map((topic) => (
          <Topic
            key={topic.get('title')}
            topic={topic}
            selectedTopics={selectedTopics}
            onSelect={() => setTopics(topic)}
          />
        ))}
      </ul>
    </div>
  )
}

/**
 * Onboarding Topics
 * @param {Object} props - The props
 * @param {Map} props.copy - Translation text
 * @param {List} props.selectedTopics - List of selected Topics
 * @param {Number} props.progress - Percentage of progress
 * @param {Number} props.screenNumber - Number of screen
 * @param {Function} props.setPage - Function to set next page
 * @param {Function} props.setTopics - Function to set selected topic
 * @param {Function} [props.handleBackClick]
 *  - Override for Back button (e.g. go to AI page when AI is enabled)
 * @param {Function} [props.onContinueToPlaylist]
 *  - Function to continue to playlist page
 * @param {boolean} props.hasContentImpression - Whether the user has a content impression
 * @returns {JSX.Element} - Onboarding Topics component
 */
const Topics = ({
  copy,
  selectedTopics,
  progress,
  screenNumber,
  setPage,
  setTopics,
  hasContentImpression,
  handleBackClick,
  onContinueToPlaylist,
}) => {
  const genericTerm = useSelector(selectOnboardingGenericTerms).first()
  const dispatch = useDispatch()
  const handleSkipClick = () => {
    setTopics(genericTerm)
    setPage()
  }

  const onBackClick = handleBackClick ?? (() => setPage(false))

  return (
    <React.Fragment>
      <OnboardingTitleGradient
        title={copy.get('topicsTitle')}
        description={copy.get('topicsDesc')}
        handleBackClick={onBackClick}
        handleSkipClick={handleSkipClick}
        showDone={selectedTopics.size}
        screenNumber={screenNumber}
        copy={copy}
      />
      <div className="onboarding-topics">
        <OnboardingProgressBar percentage={progress} />
        <TopicsList
          setTopics={setTopics}
          selectedTopics={selectedTopics}
        />
        {!!selectedTopics.size && (
          <>
            {hasContentImpression ? (
              <>
                <OptimizelyFeature
                  flag="onboarding_add_to_playlist"
                  variation="control"
                  isDefault={true}
                  anon={false}
                  noWrapper
                >
                  <Button
                    type={TYPES.PRIMARY}
                    onClick={() => {
                      setPage(true)
                      dispatch(sendOptimizelyFeatureEvent({ eventName: 'onboarding_completed' }))
                    }}
                    className="onboarding-topics__button"
                  >
                    {copy.get('topicsNext')}
                  </Button>
                </OptimizelyFeature>
                <OptimizelyFeature
                  flag="onboarding_add_to_playlist"
                  variation="show_add_to_playlist"
                  noWrapper
                >
                  <Button
                    type={TYPES.PRIMARY}
                    onClick={() => {
                      onContinueToPlaylist()
                      dispatch(sendOptimizelyFeatureEvent({ eventName: 'onboarding_completed' }))
                    }}
                    className="onboarding-topics__button"
                  >
                    {copy.get('topicsNext')}
                  </Button>
                </OptimizelyFeature>
              </>
            ) : (
              <Button
                type={TYPES.PRIMARY}
                onClick={() => {
                  setPage(true)
                  dispatch(sendOptimizelyFeatureEvent({ eventName: 'onboarding_completed' }))
                }}
                className="onboarding-topics__button"
              >
                {copy.get('topicsNext')}
              </Button>
            )}
          </>
        )}
      </div>
    </React.Fragment>
  )
}

export default Topics
